import { HistoryAttachment } from '@/types/historyAttachment';

export interface HistoryEditor {
  id: number;
  name: string;
  type: string;
  avatar_url: string | null;
  thumbnail: string | null;
}

export interface MessageEditRecord {
  id: number;
  created_at: number;
  previous_content: string | null;
  editor?: HistoryEditor;
  previous_attachments: HistoryAttachment[] | null;
}

export interface MessageSnapshot {
  content: string | null;
  attachments: HistoryAttachment[];
}

export interface MessageEditDiffItem {
  id: number;
  created_at: number;
  editor?: HistoryEditor;
  from: MessageSnapshot;
  to: MessageSnapshot;
}

interface HistoryReductionAccumulator {
  pointer: MessageSnapshot;
  diffs: MessageEditDiffItem[];
}

export const reconstructPreviousAttachments = (
  previousAttachments: HistoryAttachment[] | null,
  currentAttachments: HistoryAttachment[]
): HistoryAttachment[] => {
  if (previousAttachments === null) {
    return currentAttachments;
  }

  const reconstructed = currentAttachments.map(a => ({ ...a }));

  const currentMap = new Map<number, number>();
  reconstructed.forEach((item, index) => {
    if (item.id) currentMap.set(item.id, index);
  });

  previousAttachments.forEach(prev => {
    const replacedId = prev.replaced_attachment_id;

    if (!replacedId) return;

    const index = currentMap.get(replacedId);

    if (index === undefined) return;

    reconstructed[index] = {
      ...reconstructed[index],
      file_url: prev.file_url,
      file_name: prev.file_name,
      content_type: prev.content_type,
      file_size: prev.file_size,
    };
  });

  return reconstructed;
};

export const calculateHistoryDiffs = (
  history: MessageEditRecord[],
  currentSnapshot: MessageSnapshot | null
): MessageEditDiffItem[] => {
  if (!currentSnapshot) {
    return [];
  }

  const sortedEdits = [...history].sort((a, b) => {
    const timeDiff = a.created_at - b.created_at;
    if (timeDiff !== 0) return timeDiff;
    return a.id - b.id;
  });

  const initialAcc: HistoryReductionAccumulator = {
    pointer: {
      content: currentSnapshot.content,
      attachments: currentSnapshot.attachments ?? [],
    },
    diffs: [],
  };

  const result = sortedEdits.reduceRight<HistoryReductionAccumulator>(
    (acc, edit) => {
      const previousContent = edit.previous_content ?? acc.pointer.content;

      const previousAttachments = reconstructPreviousAttachments(
        edit.previous_attachments,
        acc.pointer.attachments
      );

      const fromState: MessageSnapshot = {
        content: previousContent,
        attachments: previousAttachments,
      };

      acc.diffs.push({
        id: edit.id,
        created_at: edit.created_at,
        editor: edit.editor,
        from: fromState,
        to: acc.pointer,
      });

      return {
        diffs: acc.diffs,
        pointer: fromState,
      };
    },
    initialAcc
  );

  return result.diffs.reverse();
};
